import React, { useId } from 'react';
import { clsx } from 'clsx';

export interface AppStoreBadgesProps {
  playStoreUrl: string;
  appStoreUrl: string;
  size?: 'sm' | 'md' | 'lg';
  layout?: 'row' | 'column';
  className?: string;
}

const sizeMap = {
  sm: 'h-10 w-auto',
  md: 'h-12 w-auto',
  lg: 'h-14 w-auto',
};

const GooglePlayBadge: React.FC<{ className?: string }> = ({ className }) => {
  const uid = useId().replace(/:/g, '');
  return (
    <svg viewBox="0 0 135 40" className={className} role="img" aria-label="Get it on Google Play">
      <rect width="135" height="40" rx="5" fill="#000" />
      <text x="47.5" y="10.5" fill="#fff" fontSize="5" fontFamily="system-ui, sans-serif" letterSpacing="0.8">GET IT ON</text>
      <text x="47.5" y="27" fill="#fff" fontSize="13" fontFamily="system-ui, sans-serif" fontWeight="500" letterSpacing="-0.2">Google Play</text>
      {/* Play Store triangle icon */}
      <g transform="translate(10, 3) scale(0.85)">
        <defs>
          <linearGradient id={`${uid}-gp-a`} x1="21.8" y1="33.29" x2="5.02" y2="16.5" gradientUnits="userSpaceOnUse">
            <stop offset="0" stopColor="#00A0FF" />
            <stop offset=".01" stopColor="#00A1FF" />
            <stop offset=".26" stopColor="#00BEFF" />
            <stop offset=".51" stopColor="#00D2FF" />
            <stop offset=".76" stopColor="#00DFFF" />
            <stop offset="1" stopColor="#00E3FF" />
          </linearGradient>
          <linearGradient id={`${uid}-gp-b`} x1="33.83" y1="20" x2="9.64" y2="20" gradientUnits="userSpaceOnUse">
            <stop offset="0" stopColor="#FFE000" />
            <stop offset=".41" stopColor="#FFBD00" />
            <stop offset=".78" stopColor="#FFA500" />
            <stop offset="1" stopColor="#FF9C00" />
          </linearGradient>
          <linearGradient id={`${uid}-gp-c`} x1="24.83" y1="22.3" x2="2.07" y2="45.05" gradientUnits="userSpaceOnUse">
            <stop offset="0" stopColor="#FF3A44" />
            <stop offset="1" stopColor="#C31162" />
          </linearGradient>
          <linearGradient id={`${uid}-gp-d`} x1="7.3" y1="0" x2="17.46" y2="10.17" gradientUnits="userSpaceOnUse">
            <stop offset="0" stopColor="#32A071" />
            <stop offset=".07" stopColor="#2DA771" />
            <stop offset=".48" stopColor="#15CF74" />
            <stop offset=".8" stopColor="#06E775" />
            <stop offset="1" stopColor="#00F076" />
          </linearGradient>
        </defs>
        <path d="M10.44 7.54a2 2 0 0 0-.46 1.4v22.12a2 2 0 0 0 .46 1.4l.07.07L22.83 20.2v-.4L10.51 7.47z" fill={`url(#${uid}-gp-a)`} />
        <path d="M26.95 24.3l-4.12-4.1v-.4l4.12-4.12.09.05 4.88 2.77c1.4.79 1.4 2.09 0 2.88l-4.88 2.77z" fill={`url(#${uid}-gp-b)`} />
        <path d="M27.04 24.25L22.83 20 10.44 32.46a1.63 1.63 0 0 0 2.08.06l14.52-8.27" fill={`url(#${uid}-gp-c)`} />
        <path d="M27.04 15.75L12.52 7.48a1.63 1.63 0 0 0-2.08.06L22.83 20z" fill={`url(#${uid}-gp-d)`} />
      </g>
    </svg>
  );
};

const AppStoreBadge: React.FC<{ className?: string }> = ({ className }) => (
  <svg viewBox="0 0 120 40" className={className} role="img" aria-label="Download on the App Store">
    <rect width="120" height="40" rx="5" fill="#000" />
    <text x="37" y="10.5" fill="#fff" fontSize="5" fontFamily="system-ui, sans-serif" letterSpacing="0.4">Download on the</text>
    <text x="37" y="27" fill="#fff" fontSize="14" fontFamily="system-ui, sans-serif" fontWeight="500" letterSpacing="-0.3">App Store</text>
    {/* Apple logo */}
    <g transform="translate(10, 6.5)" fill="#fff">
      <path d="M18.93 10.06a5.52 5.52 0 0 1 2.62-4.63 5.65 5.65 0 0 0-4.45-2.4c-1.87-.2-3.69 1.11-4.64 1.11-1 0-2.48-1.1-4.1-1.06a6.05 6.05 0 0 0-5.09 3.1c-2.2 3.8-.56 9.38 1.55 12.46 1.06 1.5 2.29 3.19 3.91 3.13 1.58-.06 2.17-1 4.08-1 1.88 0 2.43 1 4.08.98 1.7-.03 2.76-1.52 3.78-3.04a12.4 12.4 0 0 0 1.72-3.51 5.32 5.32 0 0 1-3.46-5.14zM15.73 1.24A5.42 5.42 0 0 0 17 -2.73a5.52 5.52 0 0 0-3.57 1.85 5.15 5.15 0 0 0-1.3 3.78 4.56 4.56 0 0 0 3.6-1.66z" transform="translate(0, 5)" />
    </g>
  </svg>
);

export const AppStoreBadges: React.FC<AppStoreBadgesProps> = ({
  playStoreUrl,
  appStoreUrl,
  size = 'md',
  layout = 'row',
  className,
}) => {
  const heightClass = sizeMap[size];

  return (
    <div
      className={clsx(
        'flex gap-3',
        layout === 'column' ? 'flex-col items-start' : 'flex-row flex-wrap items-center',
        className,
      )}
    >
      <a
        href={playStoreUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-block hover:opacity-80 transition-opacity"
      >
        <GooglePlayBadge className={heightClass} />
      </a>
      <a
        href={appStoreUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-block hover:opacity-80 transition-opacity"
      >
        <AppStoreBadge className={heightClass} />
      </a>
    </div>
  );
};
